import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { ReportTranslationKeys } from 'intl/keys';
import Popper, { PopperProps } from '@mui/material/Popper';
import { FormFieldProps } from 'components/inspection/Form/types';
import { BaseReportFormField, BaseReportModel } from 'shared/domain/report/types/model/common';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { ReportFormPresentational } from './presentational';
import { FieldSize } from 'shared/types/fieldSize';
import { IssueParams } from 'components/common/forms/EditableField/types';
import { ReportMessageIncludeInPdfCheckbox } from 'components/dataCreationForms/report/formUi';
import { ReportType } from 'shared/domain/report/types/model/common';
import { isInspectionReportType } from 'shared/domain/report/types/model/reportTypeGuards';
import { useReportSectionAvailability } from 'components/dataCreationForms/report/contexts/ReportSectionAvailabilityContext';
import { hasAnyReportContentSectionAvailable } from 'components/dataCreationForms/report/model';

function DialogPopper(
  props: PopperProps & { anchorEl: any }
): JSX.Element {
  const [zIndex, setZIndex] = useState<number | undefined>();
  useEffect(() => {
    const context = props.anchorEl.closest('[style*=z-index]');
    if (context) {
      setZIndex(parseInt(context.style.zIndex) + 1);
    }
  }, [props.anchorEl]);

  const style = useMemo(() => {
    return { ...props.style, zIndex: zIndex };
  }, [props.style, zIndex]);
  return <Popper {...props} placement='bottom' style={style} />;
}

function MessagePresentationField(): ReactElement | null {
  const { values, setValues } = useInputForm();
  const presentation = values.messagePresentation ?? {
    inEmailNotification: true,
    inPdfReport: false,
  };

  const message = values[BaseReportFormField.message];
  const hasMessage = typeof message === 'string' && message.trim().length > 0;

  // Without a comment there is nothing to place in the PDF, so keep the flag
  // reset while the field is empty to avoid a hidden "true" sneaking through.
  useEffect(() => {
    if (!hasMessage && presentation.inPdfReport) {
      setValues(BaseReportFormField.messagePresentation, {
        inEmailNotification: true,
        inPdfReport: false,
      });
    }
  }, [hasMessage, presentation.inPdfReport, setValues]);

  if (!hasMessage) {
    return null;
  }

  return (
    <ReportMessageIncludeInPdfCheckbox
      checked={presentation.inPdfReport}
      onChange={(checked) =>
        setValues(BaseReportFormField.messagePresentation, {
          inEmailNotification: true,
          inPdfReport: checked,
        })
      }
    />
  );
}

export function ReportForm(props: {
  onSubmit: () => void;
  onCancel: () => void;
  isPosting: boolean;
  ContentSelection: () => ReactElement | null;
  issueParams: IssueParams | null;
  reportType: ReportType;
}): ReactElement {
  const {
    onSubmit,
    onCancel,
    isPosting,
    ContentSelection,
    issueParams,
    reportType,
  } = props;

  const availability = useReportSectionAvailability();

  const hasAdditionalContent = useMemo(
    () =>
      isInspectionReportType(reportType)
        ? true
        : hasAnyReportContentSectionAvailable(availability, reportType),
    [availability, reportType]
  );

  const recipientsFieldProps: FormFieldProps<BaseReportModel> = useMemo(
    () => ({
      formKey: BaseReportFormField.recipients,
      required: true,
      labelId: ReportTranslationKeys.sendReportLabelTo,
      fieldName: 'text-field-report-recipients',
      PopperComponent: DialogPopper,
      dense: false,
      reserveSpaceForHelperText: false,
      'data-qa': 'report_recipients_field',
      issueParams,
    }),
    [issueParams]
  );
  const nameFieldProps: FormFieldProps<BaseReportModel> = useMemo(
    () => ({
      formKey: BaseReportFormField.name,
      required: true,
      labelId: ReportTranslationKeys.sendReportLabelName,
      fieldName: 'text-field-report-name',
      minLength: 3,
      maxLength: FieldSize.M,
      localStorageKey: 'report-name',
      dense: false,
      reserveSpaceForHelperText: false,
      'data-qa': 'report_name_field',
      multiline: true,
      issueParams: null,
    }),
    []
  );
  const messageFieldProps: FormFieldProps<BaseReportModel> = useMemo(
    () => ({
      formKey: BaseReportFormField.message,
      labelId: ReportTranslationKeys.messageForReport,
      required: false,
      fieldName: 'text-field-report-message',
      localStorageKey: 'report-emailMessage',
      dense: false,
      maxLength: FieldSize.XL,
      reserveSpaceForHelperText: false,
      'data-qa': 'report_message_field',
      multiline: true,
      minRows: 4,
      issueParams: null,
    }),
    []
  );

  const messagePresentation = <MessagePresentationField />;

  return (
    <ReportFormPresentational
      onSubmit={onSubmit}
      onCancel={onCancel}
      isPosting={isPosting}
      recipientsFieldProps={recipientsFieldProps}
      nameFieldProps={nameFieldProps}
      messageFieldProps={messageFieldProps}
      ContentSelection={ContentSelection}
      messagePresentation={messagePresentation}
      reportType={reportType}
      hasAdditionalContent={hasAdditionalContent}
    />
  );
}
